/**
 * DealProposalsTab - Aba "Propostas" do modal premium de negócio
 *
 * Lista as propostas comerciais do negócio (RPC get_deal_proposals),
 * permite gerar uma nova versão a partir dos produtos atuais e executar
 * as transições de situação (enviar, aceitar, recusar, cancelar).
 *
 * Regras aplicadas no servidor e refletidas na UI:
 *  - Um negócio só pode ter UMA proposta aceita;
 *  - Quando existe proposta aceita, novas gerações ficam bloqueadas;
 *  - Divergência entre a aba Produtos e o snapshot é sinalizada na linha.
 *
 * @version 1.9.1
 * RELEASE: 2026-09-03 v1.9.1 - Bloqueio do Workflow usa aviso modal global em vez de toast vermelho.
 * RELEASE: 2026-08-25 v1.9.0 - Seleção obrigatória do Tipo de proposta no modal de geração
 * RELEASE: 2026-08-12 v1.7.0 - Atalhos em ícone na linha (compartilhar, link público, aceitar, recusar, cancelar)
 * RELEASE: 2026-08-12 v1.6.0 - Recusar/Cancelar exigem confirmação com motivo obrigatório (ProposalStatusReasonDialog)
 * RELEASE: 2026-08-12 v1.5.0 - Valor após a situação + tempo real da situação (useDealProposalsRealtime)
 * RELEASE: 2026-08-12 v1.4.0 - Ícones no menu Ações (padrão do modal de detalhes) + situação como 1ª coluna
 * RELEASE: 2026-08-11 v1.3.0 - Link público persistente: gera uma vez e depois só copia/abre
 * RELEASE: 2026-08-11 v1.2.0 - Fase 6: geração do link público (landing /p/:token)
 * RELEASE: 2026-08-11 v1.1.0 - Fase 5: geração e download do PDF por proposta
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (Fase 3 do módulo de Propostas)
 */

import React, { useMemo, useState } from 'react';
import { AlertTriangle, Ban, Check, CheckCircle2, Copy, Download, ExternalLink, Eye, FileText, Link2, Loader2, Plus, RefreshCw, Send, Share2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  buildPublicProposalUrl,
  useDealProposals,
  useProposalPdf,
  useProposalShare,
  // v1.6.0: criação já com link público (e PDF em background)
  useCreateProposalWithLink,
  PROPOSAL_STATUS_COLORS,
  PROPOSAL_STATUS_LABELS,
  type DealProposal,
  type ProposalStatus,
} from '@/hooks/useDealProposals';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';
import { useWorkflowGuardCheck } from '@/hooks/useWorkflowGuardCheck';
import { WorkflowGuardGate } from '@/components/workflow/WorkflowGuardGate';

import { useDealProposalsRealtime } from '@/hooks/useDealProposalsRealtime';
// v1.9.0: tipos de proposta (cadastro por tenant, padrão "Nova venda")
import { useCrmProposalTypes } from '@/hooks/useCrmProposalTypes';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';


import { ProposalDetailsModal } from './proposals/ProposalDetailsModal';
// v1.8.0: linha compartilhada com a aba Propostas dos modais de Cliente/Contato
import { ProposalListRow } from './proposals/ProposalListRow';
import { ProposalPdfPreviewModal } from './proposals/ProposalPdfPreviewModal';
import { ProposalStatusReasonDialog } from './proposals/ProposalStatusReasonDialog';
import { ProposalShareLauncher } from './proposals/ProposalShareLauncher';


interface DealProposalsTabProps {
  /** ID do negócio */
  dealId: string;
  /** Título do negócio (sugestão de título da proposta) */
  dealTitle?: string | null;
  /** Aba visível (lazy loading das queries) */
  isActive: boolean;
  /** Permissão de criação de propostas */
  canCreate?: boolean;
  /** Permissão de edição (transições de situação) */
  canEdit?: boolean;
  /** Bloqueia ações (ex.: negócio fechado) */
  readOnly?: boolean;
}

/** Formata valores monetários */
function formatCurrency(value: number | null | undefined, currency = 'BRL'): string {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency });
}

/** Formata data curta */
function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
}

export function DealProposalsTab({
  dealId,
  dealTitle,
  isActive,
  canCreate = true,
  canEdit = true,
  readOnly = false,
}: DealProposalsTabProps) {
  const { proposals, isLoading, updateStatus } = useDealProposals(dealId, isActive);
  // v1.6.0: criação da proposta já com link público
  const { createWithLink } = useCreateProposalWithLink();
  const workflowNotice = useWorkflowGuardNotice();
  const [creating, setCreating] = useState(false);
  // v1.5.0: tempo real das propostas (situação muda pela landing pública, expiração, worker de PDF)
  useDealProposalsRealtime(dealId, isActive);
  // Geração/abertura do PDF da proposta (Fase 5)
  const { generatePdf, openPdf } = useProposalPdf();
  // Envio da proposta / link público (Fase 6)
  const { sendProposal } = useProposalShare();
  // Proposta cujo PDF está sendo gerado no momento (feedback por linha)
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);


  // Modal de geração de nova proposta
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [validDays, setValidDays] = useState('15');
  // v1.9.0: tipos de proposta ativos do tenant + seleção obrigatória
  const { activeTypes: proposalTypes, isLoading: loadingTypes } = useCrmProposalTypes();
  const [proposalTypeId, setProposalTypeId] = useState<string>('');
  // Proposta aberta no modal de detalhes
  const [detailsId, setDetailsId] = useState<string | null>(null);
  // v1.7.0: proposta em compartilhamento (atalho de ícone na listagem)
  const [shareProposalId, setShareProposalId] = useState<string | null>(null);
  // v1.6.0: transição destrutiva pendente de confirmação com motivo
  const [reasonAction, setReasonAction] = useState<{
    proposal: DealProposal;
    status: 'rejected' | 'cancelled';
  } | null>(null);
  // Proposta aberta no preview interno de PDF
  const [preview, setPreview] = useState<{ id: string; title: string; path: string | null } | null>(null);
  // Link público exibido no diálogo de compartilhamento
  const [publicLink, setPublicLink] = useState<{ url: string; number: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  /** Gera o link público (apenas quando ainda não existe) e abre o diálogo de cópia */
  const handleGenerateLink = async (proposal: DealProposal) => {
    const result = await sendProposal.mutateAsync({ proposalId: proposal.id });
    setLinkCopied(false);
    setPublicLink({ url: result.url, number: proposal.proposal_number });
    try {
      await navigator.clipboard.writeText(result.url);
      setLinkCopied(true);
    } catch {
      // Cópia automática pode ser bloqueada pelo navegador — usuário copia manualmente
    }
  };

  /** Copia o link público JÁ existente (sem gerar um novo token) */
  const handleCopyLink = async (proposal: DealProposal) => {
    if (!proposal.public_token) return;
    const url = buildPublicProposalUrl(proposal.public_token);
    setLinkCopied(false);
    setPublicLink({ url, number: proposal.proposal_number });
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success('Link copiado');
    } catch {
      // Cópia automática pode ser bloqueada pelo navegador — usuário copia manualmente
    }
  };



  /** Existe proposta aceita? Bloqueia novas gerações (regra do servidor) */
  const hasAccepted = useMemo(
    () => proposals.some((p) => p.status === 'accepted'),
    [proposals],
  );

  const canGenerate = canCreate && !readOnly && !hasAccepted;

  /*
   * v1.10.0: consulta antecipada da trava do processo. A decisão real continua
   * no banco; aqui só desabilitamos o botão e explicamos o motivo antes do clique.
   */
  const guardCriarProposta = useWorkflowGuardCheck({
    actionCode: 'crm.proposal.create',
    rootEntityType: 'crm_deal',
    rootEntityId: dealId,
    enabled: isActive && canGenerate,
  });


  /**
   * Gera a proposta com o snapshot atual dos produtos.
   * v1.6.0: já nasce com o link público ativo (e PDF solicitado em background).
   */
  const handleCreate = async () => {
    // v1.9.0: tipo é obrigatório (regra também validada no servidor)
    if (!proposalTypeId) {
      toast.error('Selecione o tipo da proposta');
      return;
    }
    setCreating(true);
    try {
      await createWithLink({
        dealId,
        title: title.trim() || null,
        validDays: Number(validDays) || 15,
        proposalTypeId,
      });
      toast.success('Proposta gerada com link de compartilhamento');
      setCreateOpen(false);
      setTitle('');
      setValidDays('15');
    } catch (error: any) {
      // Aviso modal para trava de processo; toast continua para falhas técnicas.
      if (!workflowNotice.showWorkflowGuardNotice(error)) {
        toast.error(error?.message || 'Não foi possível gerar a proposta');
      }
    } finally {
      setCreating(false);
    }
  };

  /**
   * Abre o modal de geração já com o título sugerido e o tipo pré-selecionado
   * (quando existe apenas um tipo ativo — normalmente "Nova venda").
   */
  const openCreateDialog = () => {
    setTitle(dealTitle ? `Proposta - ${dealTitle}` : '');
    setProposalTypeId((prev) =>
      prev || (proposalTypes.length === 1 ? proposalTypes[0].id : ''),
    );
    setCreateOpen(true);
  };

  /**
   * Executa transição de situação da proposta.
   * v1.6.0: recusar/cancelar são destrutivas — abrem confirmação com motivo.
   */
  const handleStatus = async (
    proposal: DealProposal,
    status: 'sent' | 'accepted' | 'rejected' | 'cancelled',
  ) => {
    if (status === 'rejected' || status === 'cancelled') {
      setReasonAction({ proposal, status });
      return;
    }
    await updateStatus.mutateAsync({ proposalId: proposal.id, status });
  };

  /** Confirma a transição destrutiva com o motivo informado */
  const handleConfirmReason = (reason: string) => {
    if (!reasonAction) return;
    updateStatus.mutate(
      { proposalId: reasonAction.proposal.id, status: reasonAction.status, reason },
      { onSuccess: () => setReasonAction(null) },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Barra de ações */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {proposals.length} proposta{proposals.length === 1 ? '' : 's'}
        </p>
        {canGenerate && (
          <WorkflowGuardGate decisao={guardCriarProposta}>
            <Button
              size="sm"
              className="h-8"
              onClick={openCreateDialog}
              disabled={guardCriarProposta.bloqueado}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Nova proposta
            </Button>
          </WorkflowGuardGate>
        )}
      </div>

      {/* Aviso quando já existe proposta aceita */}
      {hasAccepted && (
        <div className="border-l-2 border-emerald-500 pl-3 py-1">
          <p className="text-xs">
            Este negócio já possui uma proposta aceita. Para gerar uma nova, cancele a proposta
            aceita.
          </p>
        </div>
      )}

      {/* Estado vazio no padrão do sistema (borda tracejada) */}
      {proposals.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center gap-2 text-center">
          <FileText className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">Nenhuma proposta gerada</p>
          <p className="text-xs text-muted-foreground">
            As propostas são geradas a partir dos produtos e serviços da aba Produtos.
          </p>
          {canGenerate && (
            <WorkflowGuardGate decisao={guardCriarProposta}>
              <Button
                size="sm"
                variant="outline"
                className="mt-1 h-8"
                onClick={openCreateDialog}
                disabled={guardCriarProposta.bloqueado}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nova proposta
              </Button>
            </WorkflowGuardGate>
          )}
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {/* v1.8.0: a linha vem do componente compartilhado ProposalListRow,
              usado também pela aba Propostas dos modais de Cliente/Contato */}
          {proposals.map((p) => (
            <ProposalListRow
              key={p.id}
              proposal={p}
              /* v1.10.0: habilita a trava do processo nas ações da proposta */
              dealId={dealId}
              canAct={canEdit && !readOnly}
              onOpenDetails={setDetailsId}
              onShare={setShareProposalId}
              onCopyLink={handleCopyLink}
              onGenerateLink={handleGenerateLink}
              isGeneratingLink={sendProposal.isPending}
              onStatus={handleStatus}
              onPreviewPdf={(proposal, path) =>
                setPreview({
                  id: proposal.id,
                  title: proposal.title || proposal.proposal_number,
                  path,
                })
              }
              onDownloadPdf={openPdf}
              onGeneratePdf={async (proposal) => {
                setPdfBusyId(proposal.id);
                try {
                  const result = await generatePdf.mutateAsync(proposal.id);
                  if (result?.storage_path) {
                    setPreview({
                      id: proposal.id,
                      title: proposal.title || proposal.proposal_number,
                      path: result.storage_path,
                    });
                  }
                } finally {
                  setPdfBusyId(null);
                }
              }}
              pdfBusyId={pdfBusyId}
              isPdfPending={generatePdf.isPending}
            />
          ))}
        </div>
      )}


      {/* Modal de geração */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Nova proposta</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* v1.9.0: tipo da proposta (obrigatório) */}
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo da proposta *</Label>
              <Select value={proposalTypeId} onValueChange={setProposalTypeId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={loadingTypes ? 'Carregando...' : 'Selecione o tipo'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {proposalTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full ring-1 ring-border"
                          style={{ backgroundColor: t.color ?? '#9ca3af' }}
                        />
                        {t.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!loadingTypes && proposalTypes.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Nenhum tipo ativo cadastrado. Cadastre em CRM › Tipos de Proposta.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Título</Label>
              <Input
                value={title}
                maxLength={160}
                placeholder={dealTitle ? `Proposta - ${dealTitle}` : 'Título da proposta'}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Validade (dias)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={validDays}
                onChange={(e) => setValidDays(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Os produtos e serviços da aba Produtos serão copiados para a proposta e ficarão
              congelados nesta versão.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleCreate} disabled={creating || !proposalTypeId}>
              {creating && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              Gerar proposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link público gerado — exibido apenas nesta oportunidade */}
      <Dialog open={!!publicLink} onOpenChange={(open) => !open && setPublicLink(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Link público da proposta</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Envie este link ao cliente. Ele poderá visualizar a proposta {publicLink?.number},
              baixar o PDF e registrar o aceite ou a recusa.
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={publicLink?.url ?? ''} className="text-xs" />
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!publicLink) return;
                  try {
                    await navigator.clipboard.writeText(publicLink.url);
                    setLinkCopied(true);
                    toast.success('Link copiado');
                  } catch {
                    toast.error('Copie o link manualmente');
                  }
                }}
              >
                {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div className="border-l-2 border-amber-500 pl-3 py-1">
              <p className="text-xs">
                O link permanece disponível para cópia enquanto a proposta estiver ativa. Ao
                aceitar, recusar ou cancelar a proposta, o link deixa de funcionar.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" onClick={() => setPublicLink(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Modal de detalhes (snapshot + histórico) */}
      <ProposalDetailsModal proposalId={detailsId} onClose={() => setDetailsId(null)} />

      {/* v1.7.0: compartilhamento direto pela listagem (carrega o contexto da proposta) */}
      <ProposalShareLauncher
        proposalId={shareProposalId}
        onClose={() => setShareProposalId(null)}
      />

      {/* Preview interno do PDF (sem precisar baixar) */}
      <ProposalPdfPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        proposalId={preview?.id ?? null}
        title={preview?.title}
        storagePath={preview?.path ?? null}
      />

      {/* v1.6.0: confirmação com motivo para recusar/cancelar */}
      <ProposalStatusReasonDialog
        open={!!reasonAction}
        onOpenChange={(open) => !open && setReasonAction(null)}
        onConfirm={handleConfirmReason}
        isPending={updateStatus.isPending}
        title={
          reasonAction?.status === 'rejected'
            ? 'Marcar proposta como recusada'
            : 'Cancelar proposta'
        }
        description={
          reasonAction?.status === 'rejected'
            ? 'A proposta passará para a situação Recusada e deixará de valer como oferta em aberto.'
            : 'A proposta será cancelada e deixará de valer como documento comercial ativo.'
        }
        noticeText="O link público é desativado e a proposta não poderá mais ser aceita pelo cliente."
        reasonLabel={
          reasonAction?.status === 'rejected' ? 'Motivo da recusa' : 'Motivo do cancelamento'
        }
        confirmLabel={
          reasonAction?.status === 'rejected' ? 'Confirmar recusa' : 'Confirmar cancelamento'
        }
      />


    </div>
  );
}
